const formatDateTime = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");

  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
};

const isDaylightSavingTime = (date: Date): boolean => {
  // Get timezone offset for January (winter) and July (summer)
  const january = new Date(date.getFullYear(), 0, 1);
  const july = new Date(date.getFullYear(), 6, 1);

  const janOffset = january.getTimezoneOffset();
  const julOffset = july.getTimezoneOffset();

  // If current offset is different from winter offset, we're likely in DST
  // DST typically means a smaller offset (closer to UTC)
  return Math.max(janOffset, julOffset) !== date.getTimezoneOffset();
};

const getTimezoneByOffset = (date: Date): string => {
  const offset = -date.getTimezoneOffset(); // getTimezoneOffset returns negative offset
  const offsetHours = Math.floor(offset / 60);
  const offsetMinutes = Math.abs(offset % 60);

  // Check if it's daylight saving time
  const isDST = isDaylightSavingTime(date);

  // Map common timezone offsets to abbreviations - use string keys to avoid computed property warning
  const timezoneMap: {
    [key: string]: { standard: string; daylight: string };
  } = {
    "-480": { standard: "PST", daylight: "PDT" },
    "-420": { standard: "MST", daylight: "MDT" },
    "-360": { standard: "CST", daylight: "CDT" },
    "-300": { standard: "EST", daylight: "EDT" },
    "-240": { standard: "AST", daylight: "ADT" },
    "0": { standard: "GMT", daylight: "BST" },
    "60": { standard: "CET", daylight: "CEST" },
    "120": { standard: "EET", daylight: "EEST" },
    "540": { standard: "JST", daylight: "JST" },
    "480": { standard: "CST", daylight: "CST" },
    "600": { standard: "AEST", daylight: "AEDT" },
  };

  const mapping = timezoneMap[offset.toString()];
  if (mapping) {
    return isDST ? mapping.daylight : mapping.standard;
  }

  // Fallback: return UTC offset format
  const sign = offsetHours >= 0 ? "+" : "-";
  const absHours = Math.abs(offsetHours);
  const formattedOffset =
    offsetMinutes > 0
      ? `${sign}${absHours}:${String(offsetMinutes).padStart(2, "0")}`
      : `${sign}${absHours}`;

  return `UTC${formattedOffset}`;
};

const getTimezoneAbbreviation = (date: Date): string => {
  // Get timezone abbreviation from Intl.DateTimeFormat
  const formatter = new Intl.DateTimeFormat("en", {
    timeZoneName: "short",
  });

  const parts = formatter.formatToParts(date);
  const timeZonePart = parts.find((part) => part.type === "timeZoneName");

  if (timeZonePart && timeZonePart.value && timeZonePart.value !== "GMT") {
    return timeZonePart.value;
  }

  // Fallback: Get timezone using manual detection
  return getTimezoneByOffset(date);
};

export const getCurrentTimezoneTimestamp = (): string => {
  const now = new Date();

  // Get timezone info
  const timezoneAbbr = getTimezoneAbbreviation(now);

  // Format: "2025-01-14 10:22:07 (EST)" or "2025-01-14 10:22:07 (UTC-5)"
  return `${formatDateTime(now)} (${timezoneAbbr})`;
};
